import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import { computed, unref } from 'vue';

import { DATA_TYPE_TIME_UNITS } from '@/app/dashboards/constants';
import {
  buildSecondaryBreakdownOptions,
  VISUALIZATION_OPTIONS,
} from '@/app/dashboards/utils/metricBreakdown.util';
import { METRIC_TYPE_REPORT_KEYS } from '@/app/dashboards/utils/reports.enum';
import { AVAILABLE_CUSTOM_METRIC_CHANNELS } from '@/app/settings/components/CustomMetrics/constants';
import { CHANNELS, compareChannels } from '@/models/dashboards/channels.enum';
import { GRAPH_STATS } from '@/models/dashboards/graph-stats.enum';
import { MEDIA_TYPES } from '@/models/dashboards/media-types.enum';
import {
  AGGREGATION_CURRENT_GRAPH_STATS,
  AGGREGATION_NO_GRAPH_STATS,
  AGGREGATION_ONLY_AVG_GRAPH_STATS,
  AGGREGATION_TYPES,
  DATA_TYPE_FORMATS,
  DATA_TYPES,
  POST_TYPES,
  QUERY_TIMEFRAME,
} from '@/models/dashboards/metrics.constants';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useFlagStore } from '@/stores/flag';
import { useMetricsStore } from '@/stores/metrics';
import { formatIntegerWithRules, formatValueByFormatType } from '@/utils/formatters';
import { logger } from '@/utils/logger';

export function useMetricDetails({
  metric,
  channels,
  metricReportTypeKey,
  aggregateBy,
  reportVisualization,
} = {}) {
  const metricsStore = useMetricsStore();
  const dashboardReportsStore = useDashboardReportsStore();
  const flagStore = useFlagStore();

  // unref the metric details arguments before use
  const reportChannels = computed(() => unref(channels));
  const channel = computed(() => reportChannels.value?.[0]);
  const reportMetric = computed(() => unref(metric));
  const unrefMetricReportTypeKey = computed(() => unref(metricReportTypeKey));
  const unrefAggregateBy = computed(() => unref(aggregateBy));
  const unrefReportVisualization = computed(() => unref(reportVisualization));

  // Internal set up. Do not return these properties and functions
  const isContentReportMetric = computed(
    () => unrefMetricReportTypeKey.value === METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT,
  );

  const isGraphReportMetric = computed(
    () => unrefMetricReportTypeKey.value === METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  );

  const isMultiChannelMetric = computed(() => reportChannels.value?.length > 1);

  const targetMetricDetails = computed(() => {
    if (isMultiChannelMetric.value)
      return metricsStore.getTargetMetricDetailsMultiChannel(reportMetric.value);
    return metricsStore.getTargetMetricDetails(
      channel.value,
      unrefMetricReportTypeKey.value,
      reportMetric.value,
    );
  });

  // Properties and functions for ALL report types
  const allowContentTags = computed(() => targetMetricDetails.value?.allow_content_tags);

  const deprecated = computed(() => targetMetricDetails.value?.deprecated);

  const displayName = computed(() => targetMetricDetails.value?.display_name);

  const dataType = computed(() => targetMetricDetails.value?.data_type);

  const allowGoals = computed(() => targetMetricDetails.value?.allow_goals);

  const formats = computed(() => DATA_TYPE_FORMATS?.[dataType.value]);

  const formula = computed(() => targetMetricDetails.value?.formula);

  const disabledDisplayStrategies = computed(
    () => targetMetricDetails.value?.disabled_display_strategies ?? [],
  );

  const isMonetaryMetric = computed(
    () => targetMetricDetails.value?.data_type === DATA_TYPES.MONETARY,
  );

  const isIntegerMetric = computed(
    () => targetMetricDetails.value?.data_type === DATA_TYPES.INTEGER,
  );

  const isFloatMetric = computed(() => targetMetricDetails.value?.data_type === DATA_TYPES.FLOAT);

  function convertMetricToRequiredUnit(value) {
    // seconds is the preferred unit to be displayed
    if (dataType.value === DATA_TYPE_TIME_UNITS.MILLISECONDS) {
      return (value / 1000).toFixed(2);
    }
    return value;
  }

  function formatMetricValue(
    value,
    formatType,
    unit = null,
    isSmallTile = false,
    fromDashboards = false,
  ) {
    const emptyValue = '-';
    if (isNil(value)) return emptyValue;

    const format = formats.value?.[formatType];
    const convertedValue = convertMetricToRequiredUnit(value);

    let formattedValue;
    if (
      isNumber(convertedValue) &&
      format &&
      isIntegerMetric.value &&
      isFloatMetric.value &&
      fromDashboards
    ) {
      formattedValue = formatIntegerWithRules(convertedValue, isSmallTile);
    } else if (format) {
      formattedValue = formatValueByFormatType(convertedValue, format, emptyValue);
    } else {
      logger.error(`formatMetricValue: missing format for '${formatType}'`);
      formattedValue = isNumber(value) ? value.toString() : value;
    }
    if (unit) formattedValue = `${formattedValue} ${unit}`;
    return formattedValue;
  }

  const isNegativeMetric = computed(() => targetMetricDetails.value?.is_negative_metric);

  const lockedMediaBreakdown = computed(() => targetMetricDetails.value?.locked_media_breakdown);

  const supportsMediaBreakdown = computed(
    () => targetMetricDetails.value?.supports_media_breakdown,
  );

  const metricCategories = computed(() => targetMetricDetails.value?.metric_categories);

  const mediaTypeLabel = computed(() => {
    if (!supportsMediaBreakdown.value) return null;
    if (!targetMetricDetails.value?.media_type_label) return MEDIA_TYPES.ALL_MEDIA.text;
    return targetMetricDetails.value?.media_type_label;
  });

  const parentMetric = computed(
    () => targetMetricDetails.value?.parent_metric ?? reportMetric.value,
  );

  const permissions = computed(() => targetMetricDetails.value?.permissions);

  const postType = computed(() => {
    const postTypes = targetMetricDetails.value?.post_types ?? [];
    if (postTypes.length < 1) return null;
    if (postTypes.length === 1) return postTypes[0];
    return POST_TYPES.ORGANIC_PROMOTED;
  });

  const requiredFeatureFlag = computed(() => {
    if (isMultiChannelMetric.value && reportChannels.value.includes(CHANNELS.YOUTUBE.value)) {
      const ytKey =
        dashboardReportsStore.comparableMetrics?.[reportMetric.value]?.[CHANNELS.YOUTUBE.value];
      const ytDetails = metricsStore.getTargetMetricDetails(
        CHANNELS.YOUTUBE.value,
        unrefMetricReportTypeKey.value,
        ytKey,
      );
      return ytDetails?.required_feature_flag;
    }
    return targetMetricDetails.value?.required_feature_flag;
  });

  const timeframe = computed(() => {
    if (isContentReportMetric.value) return QUERY_TIMEFRAME.POSTS_PUBLISHED_IN_TIMEFRAME;
    return targetMetricDetails.value?.timeframe;
  });

  const tooltip = computed(() => targetMetricDetails.value?.description);

  // Properties and functions for CONTENT report types
  const isGalleryMetric = computed(() => targetMetricDetails.value?.is_gallery_metric ?? false);

  const sourceDataName = computed(() => {
    if (!isContentReportMetric.value) return null;
    return targetMetricDetails.value?.source_data_name;
  });

  // Properties and functions for GRAPH report types
  const aggregationType = computed(() => {
    let aggregation = targetMetricDetails.value?.aggregation;
    if (aggregation === AGGREGATION_TYPES.CUSTOM)
      aggregation = targetMetricDetails.value?.aggregation_type;
    return aggregation;
  });

  const graphBeginAtZero = computed(() => {
    if (!isGraphReportMetric.value) return null;
    return (
      aggregationType.value !== AGGREGATION_TYPES.LATEST &&
      aggregationType.value !== AGGREGATION_TYPES.LATEST_VALID
    );
  });

  const graphPrecision = computed(() => {
    if (!isGraphReportMetric.value) return null;
    if (dataType.value === DATA_TYPES.PERCENTAGE) return 4;
    return 0;
  });

  const graphStats = computed(() => {
    if (!isGraphReportMetric.value) return null;
    if (
      AGGREGATION_NO_GRAPH_STATS.includes(aggregationType.value) ||
      (AGGREGATION_ONLY_AVG_GRAPH_STATS.includes(aggregationType.value) &&
        isMultiChannelMetric.value)
    ) {
      return [];
    }
    if (AGGREGATION_ONLY_AVG_GRAPH_STATS.includes(aggregationType.value)) {
      return [GRAPH_STATS.MEAN_BY_DAY.value];
    }
    if (targetMetricDetails.value?.hide_total) {
      return [GRAPH_STATS.MEAN_BY_DAY.value];
    }
    if (AGGREGATION_CURRENT_GRAPH_STATS.includes(aggregationType.value)) {
      return [GRAPH_STATS.CURRENT.value, GRAPH_STATS.MEAN_BY_DAY.value];
    }
    return [GRAPH_STATS.SUM.value, GRAPH_STATS.MEAN_BY_DAY.value];
  });

  const connectedAccountsOnly = computed(() => targetMetricDetails.value?.connected_accounts_only);

  const dataSource = computed(() => targetMetricDetails.value?.data_source);

  const isNonAdditive = computed(() => {
    if (isMultiChannelMetric.value) {
      const firstChannelDetails = metricsStore.getTargetMetricDetails(
        channel.value,
        unrefMetricReportTypeKey.value,
        reportMetric.value,
      );
      return firstChannelDetails?.is_non_additive ?? false;
    }
    return targetMetricDetails.value?.is_non_additive ?? false;
  });

  // NOTE: Before you add another hard coded example here. Consider if it should become a property
  // in the metric details response on the backend
  const restrictedVisualizationOptions = computed(() => {
    if (reportMetric.value === 'ONLINE_FOLLOWERS') return [VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH];
    return [];
  });

  const breakdownOptions = computed(() => targetMetricDetails.value?.breakdown_options);

  const secondaryBreakdownOptions = computed(() => {
    if (!breakdownOptions.value || !unrefAggregateBy.value) return [];
    const primaryConfig = breakdownOptions.value[unrefAggregateBy.value];
    if (!primaryConfig) return [];

    const filteredOptions = buildSecondaryBreakdownOptions(
      primaryConfig.secondary_breakdown_options,
      unrefReportVisualization.value,
    );
    return filteredOptions.length > 0 ? filteredOptions : [];
  });

  function sortMetricsByChannelPriority(availableMetrics, availableChannels) {
    availableChannels.sort(compareChannels);
    return availableChannels.reduce((acc, channelName) => {
      if (availableMetrics?.[channelName]) {
        acc[channelName] = availableMetrics[channelName].sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        );
      }
      return acc;
    }, {});
  }

  function getAvailableChannelsAndMetricsForCustomMetrics({ useLegacyMetricDetails = false }) {
    const metricDetails = useLegacyMetricDetails
      ? metricsStore.legacyMetricDetails
      : metricsStore.metricDetails;
    const availableChannels = Object.keys(metricDetails).filter((availableChannel) =>
      AVAILABLE_CUSTOM_METRIC_CHANNELS.includes(availableChannel),
    );

    let availableMetrics = availableChannels.reduce((acc, availableChannel) => {
      const metricsReport = metricDetails[availableChannel].metrics_report || {};

      acc[availableChannel] = Object.entries(metricsReport)
        .filter(([, metricValue]) => metricValue.allow_in_custom_metric_formulas)
        .filter(([, metricValue]) => {
          const requiredFlag = metricValue.required_feature_flag;
          return !requiredFlag || (flagStore.ready && flagStore.flags?.[requiredFlag]);
        })
        .map(([metricKey, metricValue]) => ({
          metricKey,
          displayName: metricValue.display_name ?? metricKey,
          channel: availableChannel,
          description: metricValue.description,
        }));

      return acc;
    }, {});

    availableMetrics = sortMetricsByChannelPriority(availableMetrics, availableChannels);

    return { availableChannels, availableMetrics };
  }

  return {
    aggregationType,
    allowContentTags,
    allowGoals,
    breakdownOptions,
    connectedAccountsOnly,
    dataSource,
    restrictedVisualizationOptions,
    deprecated,
    disabledDisplayStrategies,
    displayName,
    dataType,
    formula,
    graphBeginAtZero,
    graphPrecision,
    graphStats,
    isGalleryMetric,
    isIntegerMetric,
    isNegativeMetric,
    isMonetaryMetric,
    isNonAdditive,
    lockedMediaBreakdown,
    mediaTypeLabel,
    metricCategories,
    parentMetric,
    permissions,
    postType,
    requiredFeatureFlag,
    secondaryBreakdownOptions,
    sourceDataName,
    supportsMediaBreakdown,
    timeframe,
    tooltip,
    targetMetricDetails,
    formatMetricValue,
    getAvailableChannelsAndMetricsForCustomMetrics,
  };
}
